import React, { useState, useEffect } from 'react';
import {
  Container,
  Grid,
  Card,
  CardContent,
  Typography,
  Box,
  Button,
  CircularProgress,
  Alert,
  TextField,
  Chip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions
} from '@mui/material';
import {
  Download as DownloadIcon,
  Info as InfoIcon,
  ArrowUpward as ArrowUpwardIcon
} from '@mui/icons-material';
import { useDispatch, useSelector } from 'react-redux';
import { RootState, AppDispatch } from '../../redux/store';
import {
  fetchCommissionTiers,
  fetchRevenueSettings,
  fetchPayoutSettings,
  fetchRevenueSummary,
  fetchRevenueRecords,
  setDateRange,
  setPeriod,
  RevenueRecord,
  CommissionTier
} from '../../redux/revenueSlice';
import ToastService from '../../core/notifications/ToastService';

export const RevenuePage: React.FC = () => {
  const dispatch = useDispatch<AppDispatch>();
  const {
    commissionTiers,
    revenueSettings,
    revenueSummary,
    revenueRecords,
    isLoading,
    error,
    selectedDateRange,
    selectedPeriod
  } = useSelector((state: RootState) => state.revenue);

  const [showTierInfo, setShowTierInfo] = useState(false);
  const [customStartDate, setCustomStartDate] = useState(selectedDateRange.startDate);
  const [customEndDate, setCustomEndDate] = useState(selectedDateRange.endDate);

  useEffect(() => {
    // Fetch all configuration and data
    dispatch(fetchCommissionTiers());
    dispatch(fetchRevenueSettings());
    dispatch(fetchPayoutSettings());
    dispatch(fetchRevenueSummary(selectedDateRange));
    dispatch(fetchRevenueRecords({ startDate: selectedDateRange.startDate, endDate: selectedDateRange.endDate }));
  }, [dispatch, selectedDateRange]);

  const handlePeriodChange = (period: 'today' | 'week' | 'month' | 'year' | 'custom') => {
    dispatch(setPeriod(period));
    const today = new Date();
    let startDate = new Date();

    switch (period) {
      case 'today':
        startDate = today;
        break;
      case 'week':
        startDate = new Date(today.getTime() - 7 * 24 * 60 * 60 * 1000);
        break;
      case 'month':
        startDate = new Date(today.getFullYear(), today.getMonth(), 1);
        break;
      case 'year':
        startDate = new Date(today.getFullYear(), 0, 1);
        break;
      case 'custom':
        return;
    }

    const newDateRange = {
      startDate: startDate.toISOString().split('T')[0],
      endDate: today.toISOString().split('T')[0]
    };

    dispatch(setDateRange(newDateRange));
    dispatch(fetchRevenueSummary(newDateRange));
    dispatch(fetchRevenueRecords({ startDate: newDateRange.startDate, endDate: newDateRange.endDate }));
  };

  const handleCustomDateApply = () => {
    if (customStartDate && customEndDate) {
      const newDateRange = {
        startDate: customStartDate,
        endDate: customEndDate
      };
      dispatch(setDateRange(newDateRange));
      dispatch(fetchRevenueSummary(newDateRange));
      dispatch(fetchRevenueRecords({ startDate: newDateRange.startDate, endDate: newDateRange.endDate }));
    }
  };

  const handleExportCSV = () => {
    if (revenueRecords.length === 0) {
      ToastService.warning('No revenue records to export');
      return;
    }

    const headers = ['Booking ID', 'Gross Amount', 'Platform Fee', 'Commission', 'Tax', 'Net Amount', 'Date', 'Status'];
    const rows = revenueRecords.map((record: RevenueRecord) => [
      record.bookingId,
      record.grossAmount,
      record.platformFee,
      record.commission,
      record.tax,
      record.netAmount,
      record.date,
      record.status
    ]);

    const csv = [headers, ...rows].map(row => row.join(',')).join('\n');
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `revenue-${selectedDateRange.startDate}-to-${selectedDateRange.endDate}.csv`;
    a.click();
    window.URL.revokeObjectURL(url);

    ToastService.success('Revenue data exported successfully');
  };

  const getStatusColor = (status: string) => {
    const colors: Record<string, string> = {
      completed: '#10B981',
      pending: '#F59E0B',
      refunded: '#EF4444'
    };
    return colors[status] || '#6B7280';
  };

  if (error) {
    return (
      <Container sx={{ py: 4 }}>
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      </Container>
    );
  }

  return (
    <Container maxWidth="lg" sx={{ py: 4 }}>
      {/* Header */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 4 }}>
        <Box>
          <Typography variant="h4" sx={{ fontWeight: 'bold', mb: 1 }}>
            Revenue Dashboard
          </Typography>
          <Typography variant="body2" sx={{ color: '#666' }}>
            Track your earnings and commission details
          </Typography>
        </Box>
        <Button
          variant="outlined"
          startIcon={<DownloadIcon />}
          onClick={handleExportCSV}
          disabled={isLoading}
        >
          Export CSV
        </Button>
      </Box>

      {/* Period Selector */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', alignItems: 'center' }}>
            {(['today', 'week', 'month', 'year', 'custom'] as const).map(period => (
              <Button
                key={period}
                variant={selectedPeriod === period ? 'contained' : 'outlined'}
                onClick={() => handlePeriodChange(period)}
                sx={{
                  backgroundColor: selectedPeriod === period ? '#14B8A6' : 'transparent',
                  textTransform: 'capitalize'
                }}
              >
                {period}
              </Button>
            ))}

            {selectedPeriod === 'custom' && (
              <Box sx={{ display: 'flex', gap: 1, ml: 2 }}>
                <TextField
                  type="date"
                  value={customStartDate}
                  onChange={(e) => setCustomStartDate(e.target.value)}
                  size="small"
                />
                <TextField
                  type="date"
                  value={customEndDate}
                  onChange={(e) => setCustomEndDate(e.target.value)}
                  size="small"
                />
                <Button variant="contained" onClick={handleCustomDateApply}>
                  Apply
                </Button>
              </Box>
            )}
          </Box>
        </CardContent>
      </Card>

      {isLoading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
          <CircularProgress />
        </Box>
      ) : (
        <>
          {/* Summary Cards */}
          {revenueSummary && (
            <Grid container spacing={3} sx={{ mb: 4 }}>
              {/* Gross Revenue */}
              <Grid item xs={12} sm={6} md={3}>
                <Card sx={{ backgroundColor: '#f0fffe', borderLeft: '4px solid #14B8A6' }}>
                  <CardContent>
                    <Typography variant="body2" sx={{ color: '#666', mb: 1 }}>
                      Gross Revenue
                    </Typography>
                    <Typography variant="h5" sx={{ fontWeight: 'bold', mb: 1 }}>
                      {revenueSettings?.currencySymbol}{revenueSummary.totalGrossRevenue.toFixed(2)}
                    </Typography>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                      <ArrowUpwardIcon sx={{ fontSize: 16, color: '#10B981' }} />
                      <Typography variant="caption" sx={{ color: '#10B981' }}>
                        +{((revenueSummary.totalGrossRevenue / 100) * 5).toFixed(2)}% this period
                      </Typography>
                    </Box>
                  </CardContent>
                </Card>
              </Grid>

              {/* Commission */}
              <Grid item xs={12} sm={6} md={3}>
                <Card sx={{ backgroundColor: '#fff5f0', borderLeft: '4px solid #F59E0B' }}>
                  <CardContent>
                    <Typography variant="body2" sx={{ color: '#666', mb: 1 }}>
                      Commission (HUSLRZ)
                    </Typography>
                    <Typography variant="h5" sx={{ fontWeight: 'bold', mb: 1 }}>
                      {revenueSettings?.currencySymbol}{revenueSummary.totalCommission.toFixed(2)}
                    </Typography>
                    <Typography variant="caption" sx={{ color: '#666' }}>
                      {revenueSummary.currentTierName} Tier
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>

              {/* Tax */}
              <Grid item xs={12} sm={6} md={3}>
                <Card sx={{ backgroundColor: '#f5f3ff', borderLeft: '4px solid #8B5CF6' }}>
                  <CardContent>
                    <Typography variant="body2" sx={{ color: '#666', mb: 1 }}>
                      Tax (GST)
                    </Typography>
                    <Typography variant="h5" sx={{ fontWeight: 'bold', mb: 1 }}>
                      {revenueSettings?.currencySymbol}{revenueSummary.totalTax.toFixed(2)}
                    </Typography>
                    <Typography variant="caption" sx={{ color: '#666' }}>
                      {revenueSettings?.taxPercentage}% on commission
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>

              {/* Net Revenue */}
              <Grid item xs={12} sm={6} md={3}>
                <Card sx={{ backgroundColor: '#f0fdf4', borderLeft: '4px solid #10B981' }}>
                  <CardContent>
                    <Typography variant="body2" sx={{ color: '#666', mb: 1 }}>
                      Net Revenue (Yours)
                    </Typography>
                    <Typography variant="h5" sx={{ fontWeight: 'bold', mb: 1, color: '#10B981' }}>
                      {revenueSettings?.currencySymbol}{revenueSummary.totalNetRevenue.toFixed(2)}
                    </Typography>
                    <Typography variant="caption" sx={{ color: '#666' }}>
                      After all deductions
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
            </Grid>
          )}

          {/* Commission Tier Info */}
          <Card sx={{ mb: 4 }}>
            <CardContent>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
                  Commission Tier Information
                </Typography>
                <Button
                  size="small"
                  startIcon={<InfoIcon />}
                  onClick={() => setShowTierInfo(true)}
                >
                  View All Tiers
                </Button>
              </Box>

              {revenueSummary && (
                <Box>
                  <Typography variant="body2" sx={{ mb: 2 }}>
                    <strong>Current Tier:</strong> {revenueSummary.currentTierName}
                  </Typography>
                  <Typography variant="body2" sx={{ mb: 2 }}>
                    <strong>Current Month Revenue:</strong> {revenueSettings?.currencySymbol}{revenueSummary.currentMonthRevenue.toFixed(2)}
                  </Typography>

                  {revenueSummary.nextTierName && revenueSummary.revenueToNextTier && (
                    <Box sx={{ p: 2, backgroundColor: '#f0fffe', borderRadius: 1 }}>
                      <Typography variant="body2" sx={{ mb: 1 }}>
                        <strong>Next Tier:</strong> {revenueSummary.nextTierName}
                      </Typography>
                      <Typography variant="body2" sx={{ color: '#14B8A6', fontWeight: 'bold' }}>
                        {revenueSettings?.currencySymbol}{revenueSummary.revenueToNextTier.toFixed(2)} more to upgrade
                      </Typography>
                    </Box>
                  )}
                </Box>
              )}
            </CardContent>
          </Card>

          {/* Revenue Records Table */}
          <Card>
            <CardContent>
              <Typography variant="h6" sx={{ fontWeight: 'bold', mb: 2 }}>
                Revenue Records
              </Typography>

              {revenueRecords.length === 0 ? (
                <Typography variant="body2" sx={{ color: '#999', textAlign: 'center', py: 4 }}>
                  No revenue records found for the selected period
                </Typography>
              ) : (
                <TableContainer component={Paper} sx={{ backgroundColor: '#fff' }}>
                  <Table>
                    <TableHead sx={{ backgroundColor: '#f5f5f5' }}>
                      <TableRow>
                        <TableCell sx={{ fontWeight: 'bold' }}>Booking ID</TableCell>
                        <TableCell align="right" sx={{ fontWeight: 'bold' }}>Gross Amount</TableCell>
                        <TableCell align="right" sx={{ fontWeight: 'bold' }}>Platform Fee</TableCell>
                        <TableCell align="right" sx={{ fontWeight: 'bold' }}>Commission</TableCell>
                        <TableCell align="right" sx={{ fontWeight: 'bold' }}>Tax</TableCell>
                        <TableCell align="right" sx={{ fontWeight: 'bold' }}>Net Amount</TableCell>
                        <TableCell sx={{ fontWeight: 'bold' }}>Date</TableCell>
                        <TableCell sx={{ fontWeight: 'bold' }}>Status</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {revenueRecords.map((record: RevenueRecord) => (
                        <TableRow key={record.id} sx={{ '&:hover': { backgroundColor: '#f9f9f9' } }}>
                          <TableCell>#{record.bookingId}</TableCell>
                          <TableCell align="right">{revenueSettings?.currencySymbol}{record.grossAmount.toFixed(2)}</TableCell>
                          <TableCell align="right">{revenueSettings?.currencySymbol}{record.platformFee.toFixed(2)}</TableCell>
                          <TableCell align="right">{revenueSettings?.currencySymbol}{record.commission.toFixed(2)}</TableCell>
                          <TableCell align="right">{revenueSettings?.currencySymbol}{record.tax.toFixed(2)}</TableCell>
                          <TableCell align="right" sx={{ fontWeight: 'bold', color: '#10B981' }}>
                            {revenueSettings?.currencySymbol}{record.netAmount.toFixed(2)}
                          </TableCell>
                          <TableCell>{record.date}</TableCell>
                          <TableCell>
                            <Chip
                              label={record.status}
                              size="small"
                              sx={{
                                backgroundColor: getStatusColor(record.status) + '20',
                                color: getStatusColor(record.status),
                                fontWeight: 'bold'
                              }}
                            />
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              )}
            </CardContent>
          </Card>
        </>
      )}

      {/* Commission Tier Dialog */}
      <Dialog open={showTierInfo} onClose={() => setShowTierInfo(false)} maxWidth="md" fullWidth>
        <DialogTitle>Commission Tiers</DialogTitle>
        <DialogContent sx={{ pt: 2 }}>
          <Typography variant="body2" sx={{ mb: 3, color: '#666' }}>
            Your commission tier is determined by your monthly revenue. Higher revenue means lower commission percentage.
          </Typography>

          {commissionTiers.length === 0 ? (
            <Typography variant="body2" sx={{ color: '#999' }}>
              Loading commission tiers...
            </Typography>
          ) : (
            <TableContainer component={Paper}>
              <Table>
                <TableHead sx={{ backgroundColor: '#f5f5f5' }}>
                  <TableRow>
                    <TableCell sx={{ fontWeight: 'bold' }}>Tier Name</TableCell>
                    <TableCell align="right" sx={{ fontWeight: 'bold' }}>Min Revenue</TableCell>
                    <TableCell align="right" sx={{ fontWeight: 'bold' }}>Max Revenue</TableCell>
                    <TableCell align="right" sx={{ fontWeight: 'bold' }}>Commission %</TableCell>
                    <TableCell sx={{ fontWeight: 'bold' }}>Description</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {commissionTiers.map((tier: CommissionTier) => (
                    <TableRow key={tier.id}>
                      <TableCell sx={{ fontWeight: 'bold' }}>{tier.name}</TableCell>
                      <TableCell align="right">{revenueSettings?.currencySymbol}{tier.minMonthlyRevenue.toFixed(2)}</TableCell>
                      <TableCell align="right">
                        {tier.maxMonthlyRevenue ? `${revenueSettings?.currencySymbol}${tier.maxMonthlyRevenue.toFixed(2)}` : 'Unlimited'}
                      </TableCell>
                      <TableCell align="right" sx={{ fontWeight: 'bold', color: '#14B8A6' }}>
                        {tier.commissionPercentage}%
                      </TableCell>
                      <TableCell>{tier.description}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowTierInfo(false)}>Close</Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default RevenuePage;
